import styled from "@emotion/styled";
import {
  ArrowBack,
  HelpOutline,
  KeyboardArrowDown,
  Menu
} from "@mui/icons-material";
import {
  Avatar,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography
} from "@mui/material";
import Background1 from "assets/images/qb-1.jpg";
import Background2 from "assets/images/qb-2.jpg";
import Background3 from "assets/images/qb-3.jpg";
import Background4 from "assets/images/qb-4.jpg";
import { AccountMenu, SignOutModal } from "components/UI";
import { IMAGE_URL } from "config";
import i18next from "i18next";
import { useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useCentralStore } from "stores";

const supportedLanguages = [
  {
    name: "English",
    locale: "en"
  },
  {
    name: "አማርኛ",
    locale: "am"
  },
  {
    name: "Afan Oromo",
    locale: "or"
  }
];

const getBackgroundImage = () => {
  const backgrounds = [Background1, Background2, Background3, Background4];
  const dayOfMonth = new Date().getDate();
  return backgrounds[dayOfMonth % backgrounds.length];
};

export const QuickBoardLayout = () => {
  const backgroundImage = getBackgroundImage();

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        height: "100vh",
        overflow: "hidden",
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed"
      }}
    >
      <SignOutModal />
      <Header />
      <Stack sx={{ flex: 1, overflow: "auto" }}>
        <Outlet />
      </Stack>
    </Stack>
  );
};

export function Header({ hasBack = true }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const headerTitle = useCentralStore(state => state.headerTitle);
  const openDrawer = useCentralStore(state => state.openDrawer);
  const navigate = useNavigate();
  const auth = useAuthUser();
  const { t } = useTranslation();

  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        color: "neutral.contrastText",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(8px)",
        py: {
          xs: 1,
          sm: 1.5,
          md: 2
        },
        px: {
          xs: 1.5,
          sm: 2,
          md: 4
        }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }}>
        {hasBack && (
          <IconButton
            aria-label="back"
            onClick={() => navigate(-1)}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              p: { sm: 1, md: 1.5 }
            }}
          >
            <ArrowBack sx={{ color: "#ffffff" }} />
          </IconButton>
        )}
        <IconButton
          aria-label="menu"
          onClick={openDrawer}
          sx={{
            display: { xs: "none", md: "none" },
            p: { xs: 0.5, sm: 1 }
          }}
        >
          <Menu />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 500,
            color: "#fff"
          }}
        >
          {headerTitle}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={{
          xs: 0.5,
          sm: 1,
          md: 2,
          lg: 3
        }}
        alignItems="center"
      >
        {/* <NotificationsNone /> */}
        <Button
          variant="text"
          component={Link}
          to="/help-center"
          startIcon={<HelpOutline />}
          sx={{
            color: "#ffffff",
            display: { xs: "none", lg: "flex" },
            px: { md: 1, lg: 2 }
          }}
        >
          {t("Help.Help.HelpCenter")}
        </Button>
        <IconButton
          component={Link}
          to="/help-center"
          sx={{
            display: { xs: "none", sm: "inline-flex", lg: "none" },
            color: "#ffffff",
            p: { xs: 0.5, sm: 1 }
          }}
        >
          <HelpOutline />
        </IconButton>
        <LanguageSelector />
        <Typography
          sx={{
            color: "#ffffff",
            display: { xs: "none", md: "block" }
          }}
        >
          {auth()?.first_name} {auth()?.last_name}
        </Typography>
        <Button
          onClick={handleClick}
          size="small"
          endIcon={
            <KeyboardArrowDown
              sx={{
                color: "#ffffff",
                display: { xs: "none", sm: "block" }
              }}
            />
          }
          sx={{
            minWidth: "auto",
            p: { xs: 0.5, sm: 1 }
          }}
        >
          <Avatar
            src={auth()?.image_url ? `${IMAGE_URL}/${auth()?.image_url}` : ""}
            alt={auth()?.first_name}
            sx={{
              border: "2px solid",
              borderColor: "#ffffff",
              width: { xs: 32, sm: 36, md: 40 },
              height: { xs: 32, sm: 36, md: 40 }
            }}
          >
            {auth()?.first_name?.[0]}
          </Avatar>
        </Button>
      </Stack>
      <Popover
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 2,
          sx: {
            borderRadius: 4,
            boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)"
          }
        }}
      >
        <AccountMenu />
      </Popover>
    </Stack>
  );
}

export const LanguageSelector = () => {
  const [currentLanguage, setCurrentLanguage] = useState(i18next.language);

  return (
    <Languages
      value={currentLanguage}
      onChange={e => {
        i18next.changeLanguage(e.target.value);
        setCurrentLanguage(e.target.value);
      }}
    >
      {supportedLanguages.map(supportedLanguage => (
        <Language key={supportedLanguage.name} value={supportedLanguage.locale}>
          {supportedLanguage.name}
        </Language>
      ))}
    </Languages>
  );
};

const Languages = styled.select`
  border: none;
  outline: none;
  background-color: transparent;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
  font-size: 0.875rem;

  @media (min-width: 600px) {
    font-size: 0.9375rem;
  }

  @media (min-width: 900px) {
    font-size: 1rem;
  }
`;

const Language = styled.option``;
